import { cn } from '@invent/wl-ui-kit-next';
import { Alert, AlertDescription, AlertTitle, Button, Checkbox } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { DataTable, FileChoice, HelpPopover, NotPermitted, type IDataColumn } from '@/design';
import { ApiError } from '@/domains/api';
import type {
  IRankingImportDerived,
  IRankingImportFlag,
  IRankingImportIssue,
  IRankingImportReport
} from '../../api/ranking-process-contracts';
import { rankingProcessApi } from '../../api/ranking-process-api';
import { RankingFlags } from '../../components/ranking-flags';
import { wordFor } from '../../figures';
import { rankingPath } from '../../paths';
import {
  RANKING_FLAG_WORDS,
  RANKING_REFUSAL_WORDS,
  countText,
  rankingRefusalReason
} from '../../rankings';
import { refusalMessage } from '../../refusal';
import { sentence } from '../person-hub/said';
import { CycleChoice, useCycleChoice } from './cycle-choice';
import { FactStrip, useRanking } from './ranking';

/**
 * A ranking's Import scores tab (SR-231, design §4–§5): a scored result's file, checked first and applied
 * second — both the same `POST …/import`, "Check file" with `dryRun=true`, "Apply" with
 * `dryRun=false` and the same file and `compact`, `acceptWarnings` as ticked.
 *
 * **The report is the one survey-api answered for the file and switch on screen**: choosing another
 * file or flipping "Close gaps" drops it, so Apply never sends what was not checked. Apply is
 * disabled while an error stands, and while a warning does until "I accept the warnings" is ticked
 * — a press that can only be refused is not offered. **The rows the list's criteria flag** (SR-506:
 * gender, age, territory) are not warnings and ask for nothing: one summary line, its list folded
 * until opened, and — once applied — a link to the Placements tab's Flagged filter. survey-api judges every row; nothing here
 * reads the file.
 *
 * **The ranking's cycle is the file's** (SR-415): the export writes its cycle on every row, in a
 * Cycle column the scored result keeps, and the import links a draft on no cycle to it; a draft's
 * cycle is fixed (SR-451), so a file of another cycle is refused. A file without the column takes
 * the draft's own cycle, or, on an edition on none, the one chosen here (`cycle-choice.tsx`, one
 * choice with the Export tab), sent as `cycle` only where it is not the link already. The report
 * says the cycle and whether the file named it. A changed choice drops a report not yet applied,
 * as a changed file does; an applied one stays, since its apply moves the link and so the choice.
 * "Check file" waits for the cycles' read, which says what is sent.
 *
 * Refusals are said as `role="alert"`: the file's (400 `invalid-file`, 413, 415) in survey-api's
 * words, and `ranking-published` / `ranking-changed` in ours. A published ranking takes no import,
 * so its tab says that and offers nothing.
 */

interface IChecked {
  file: File;
  compact: boolean;
  cycle: Nullable<string>;
  report: IRankingImportReport;
}

export function RankingImportTab() {
  const record = useRanking();
  const choice = useCycleChoice();
  const [file, setFile] = React.useState<Nullable<File>>(null);
  const [compact, setCompact] = React.useState(false);
  const [accept, setAccept] = React.useState(false);
  const [checked, setChecked] = React.useState<Nullable<IChecked>>(null);
  const [busy, setBusy] = React.useState<Nullable<'check' | 'apply'>>(null);
  const [refusal, setRefusal] = React.useState<Nullable<string>>(null);
  const [applied, setApplied] = React.useState<Nullable<string>>(null);

  if (!record.permitted) return <NotPermitted what="importing a ranking result" />;
  const refused = refusal && (
    <Alert variant="destructive">
      <AlertTitle>Not imported</AlertTitle>
      <AlertDescription>{refusal}</AlertDescription>
    </Alert>
  );
  if (record.overview?.ranking.status === 'published') {
    return (
      <div className="grid max-w-5xl gap-5">
        {refused}
        <p className="text-sm text-muted-foreground">
          This ranking is published, so it takes no import. “Back to draft…” in the header makes it
          a draft again, and an import then replaces every placement.
        </p>
      </div>
    );
  }

  const report =
    checked &&
    checked.file === file &&
    checked.compact === compact &&
    (checked.cycle === choice.code || checked.report.applied)
      ? checked.report
      : null;
  // The cycle a file without a Cycle column is linked to: none to send where it is the link.
  const sentCycle = choice.code !== choice.linked ? choice.code : null;
  const cyclesRead = choice.cycles !== null || choice.error !== null;
  const errors = report?.errors.length ?? 0;
  const warnings = report?.warnings.length ?? 0;
  const appliable = report !== null && !report.applied && errors === 0 && (warnings === 0 || accept);

  const send = async (dryRun: boolean) => {
    if (!file) return;
    setBusy(dryRun ? 'check' : 'apply');
    setRefusal(null);
    setApplied(null);
    try {
      const answer = await rankingProcessApi.importResult(record.key, file, {
        dryRun,
        compact,
        acceptWarnings: !dryRun && accept,
        cycle: sentCycle
      });
      setChecked({ file, compact, cycle: choice.code, report: answer });
      if (answer.applied) {
        setApplied(
          `Applied — ${countText(answer.counts.placed)} placements written to ` +
            `${answer.ranking.name}.`
        );
        void record.reread();
      }
    } catch (failure) {
      setRefusal(importRefusal(failure));
      // A draft changed or published since the check: its report no longer holds, and the record
      // is read again — a published one then offers no import at all.
      const reason = rankingRefusalReason(failure);
      if (reason === 'ranking-changed' || reason === 'ranking-published') {
        setChecked(null);
        void record.reread();
      }
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="grid max-w-5xl gap-5">
      <div className="grid gap-3 rounded-md border border-border bg-white p-4">
        <p className="text-sm text-muted-foreground">
          The file must be of this edition’s cycle — the Cycle column the export writes on every
          row; a file of another cycle is refused. The Snapshot column beside it says which
          snapshot the file was scored on: a file of an earlier one than the ranking holds now is
          said as a warning before it is applied.
        </p>
        <CycleChoice choice={choice} tab="import" />
        <FileChoice
          label="Scored result (CSV or XLSX, at most 10 MB)"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          file={file}
          onChoose={(next) => {
            setFile(next);
            setAccept(false);
            setApplied(null);
            setRefusal(null);
          }}
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={compact}
            onCheckedChange={(next) => {
              setCompact(next === true);
              setAccept(false);
            }}
          />
          Close gaps — number each territory 1..N in the file’s order of place
        </label>
        <div>
          <Button type="button" size="sm" disabled={!file || busy !== null || !cyclesRead}
            onClick={() => void send(true)}
          >
            {busy === 'check' ? 'Checking…' : 'Check file'}
          </Button>
        </div>
      </div>

      {refused}
      <p role="status" className={applied ? 'text-sm text-[var(--color-teal)]' : 'sr-only'}>
        {applied ?? ''}
      </p>

      {report && (
        <section aria-label="Report" className="grid gap-4">
          <ReportCounts report={report} />
          <Issues title="Errors" issues={report.errors} />
          <Issues title="Warnings" issues={report.warnings} />
          <FlagSummary report={report} />
          <DerivedSummary report={report} />
          {!report.applied && (
            <div className="flex flex-wrap items-center gap-4">
              {warnings > 0 && (
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={accept}
                    disabled={errors > 0}
                    onCheckedChange={(next) => setAccept(next === true)}
                  />
                  I accept the warnings
                </label>
              )}
              <Button
                type="button"
                size="sm"
                disabled={!appliable || busy !== null}
                onClick={() => void send(false)}
              >
                {busy === 'apply' ? 'Applying…' : 'Apply to the draft'}
              </Button>
              {errors > 0 && (
                <span className="text-sm text-muted-foreground">
                  Fix the errors in the file and check it again.
                </span>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/** What survey-api's refusal of an import says — see the module's comment. */
function importRefusal(failure: unknown): string {
  return fileRefusal(
    failure,
    'There is no such list for this survey.',
    'The file could not be imported'
  );
}

/** What survey-api's refusal of an uploaded file says — the import's and a firm's answer's: a
 *  ranking state's reason in words, a 404 as `notFound`, the file's own refusal (`invalid-file`,
 *  413, 415) with its `detail`. */
export function fileRefusal(failure: unknown, notFound: string, fallback: string): string {
  if (failure instanceof ApiError) {
    const reason = rankingRefusalReason(failure);
    if (reason !== null) return wordFor(RANKING_REFUSAL_WORDS, reason);
    if (failure.status === 404) return notFound;
    const detail = failure.problem?.detail;
    if (detail) return sentence(`${failure.message} ${detail}`);
  }
  return sentence(refusalMessage(failure, fallback));
}

function ReportCounts({ report }: { report: IRankingImportReport }) {
  const facts: [string, string, string][] = [
    ['rows', 'Rows', countText(report.counts.rows)],
    ['placed', 'Placed', countText(report.counts.placed)],
    ['unplaced', 'Unplaced', countText(report.counts.unplaced)],
    ['territories', 'Territories', countText(report.counts.territories)]
  ];
  if (report.diff) {
    facts.push(
      ['added', 'Added', countText(report.diff.added)],
      ['removed', 'Removed', countText(report.diff.removed)],
      ['moved', 'Moved', countText(report.diff.moved)],
      ['unchanged', 'Unchanged', countText(report.diff.unchanged)]
    );
  }
  return (
    <div className="grid gap-2">
      <p className="text-sm">
        {report.applied ? 'Applied to' : 'Checked against'} <strong>{report.ranking.name}</strong>
        {report.ranking.exists ? '' : ' — a new draft'}
        {report.cycle ? (
          <span data-report-cycle={report.cycle}>
            {report.applied ? ', linked to cycle ' : ', to be linked to cycle '}
            <strong>{report.cycle}</strong>
            {report.cycleFromFile ? ' (from the file)' : ''}
          </span>
        ) : null}
      </p>
      <FactStrip hook="data-report" facts={facts} />
    </div>
  );
}

const FLAG_COLUMNS: IDataColumn<IRankingImportFlag>[] = [
  { id: 'row', header: 'Row', numeric: true, cell: (flag) => flag.row },
  { id: 'name', header: 'Participant', cell: (flag) => flag.displayName ?? flag.participantId },
  { id: 'territory', header: 'Territory', cell: (flag) => flag.territory ?? '' },
  {
    id: 'flag',
    header: 'Flag',
    cell: (flag) => (
      <span className="flex flex-wrap items-start gap-1.5">
        <RankingFlags flags={[flag]} said />
        <span className="pt-1">{flag.message}</span>
      </span>
    )
  }
];

/** The flagged rows (SR-506) in one line — "N placements flagged: gender X, age Y, territory Z" —
 *  their list folded until opened; once applied, a link to the Placements tab's Flagged filter. */
function FlagSummary({ report }: { report: IRankingImportReport }) {
  const { key } = useRanking();
  const [open, setOpen] = React.useState(false);
  const listId = React.useId();
  const flags = report.flags ?? [];
  if (flags.length === 0) return null;
  const placements = new Set(flags.map((flag) => flag.participantId)).size;
  const counts = [...RANKING_FLAG_WORDS]
    .map(([code, word]) => [word, flags.filter((flag) => flag.code === code).length] as const)
    .filter(([, count]) => count > 0)
    .map(([word, count]) => `${word} ${count.toLocaleString('en-US')}`);
  return (
    <div className="grid gap-2" data-flag-summary>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>
          <strong>{countText(placements)}</strong> {placements === 1 ? 'placement' : 'placements'}{' '}
          flagged: {counts.join(', ')}
        </span>
        <HelpPopover label="About flagged placements">
          <p>
            A flag is the advisor’s own survey data not fitting the list — gender, age or
            territory; it does not hold the import, and once applied the Placements tab’s Flagged
            filter lists every flagged row to work through.
          </p>
        </HelpPopover>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide the list' : 'Show the list'}
        </Button>
        {report.applied && (
          <Link className="text-sm underline" to={rankingPath(key, 'placements', '?flagged=1')}>
            Review in Placements
          </Link>
        )}
      </div>
      <div id={listId} hidden={!open}>
        {open && (
          <DataTable
            label="Flagged placements"
            columns={FLAG_COLUMNS}
            rows={flags}
            rowKey={(flag) => `${flag.row}-${flag.code}`}
            maxHeight="none"
            state={{ kind: 'empty', content: '' }}
          />
        )}
      </div>
    </div>
  );
}

const DERIVED_COLUMNS: IDataColumn<IRankingImportDerived>[] = [
  { id: 'row', header: 'Row', numeric: true, cell: (row) => row.row },
  { id: 'name', header: 'Participant', cell: (row) => row.displayName ?? row.participantId },
  { id: 'territory', header: 'Territory', cell: (row) => row.territory }
];

/** The rows that named no territory and took the one the office derives (SR-319) in one line,
 *  their list folded until opened — said, never holding the apply. */
function DerivedSummary({ report }: { report: IRankingImportReport }) {
  const [open, setOpen] = React.useState(false);
  const listId = React.useId();
  const derived = report.derived ?? [];
  if (derived.length === 0) return null;
  return (
    <div className="grid gap-2" data-derived-summary>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span>
          <strong>{countText(derived.length)}</strong> {derived.length === 1 ? 'row' : 'rows'} with
          no territory took the one the office gives
        </span>
        <HelpPopover label="About territories from the office">
          <p>
            A placed row with no territory takes the territory the advisor’s primary office derives
            from the list’s territories — staff’s override first. It does not hold the import.
          </p>
        </HelpPopover>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen(!open)}
        >
          {open ? 'Hide the list' : 'Show the list'}
        </Button>
      </div>
      <div id={listId} hidden={!open}>
        {open && (
          <DataTable
            label="Rows that took the office's territory"
            columns={DERIVED_COLUMNS}
            rows={derived}
            rowKey={(row) => String(row.row)}
            maxHeight="none"
            state={{ kind: 'empty', content: '' }}
          />
        )}
      </div>
    </div>
  );
}

const ISSUE_COLUMNS: IDataColumn<IRankingImportIssue & { index: number }>[] = [
  { id: 'row', header: 'Row', numeric: true, cell: (issue) => issue.row ?? '—' },
  { id: 'code', header: 'Code', className: 'font-mono text-xs', cell: (issue) => issue.code },
  { id: 'territory', header: 'Territory', cell: (issue) => issue.territory ?? '' },
  { id: 'message', header: 'Message', cell: (issue) => issue.message }
];

function Issues({ title, issues }: { title: 'Errors' | 'Warnings'; issues: IRankingImportIssue[] }) {
  return (
    <ReportTable
      title={title}
      heading={2}
      rows={issues}
      columns={ISSUE_COLUMNS}
      hook={['data-issue', (issue) => issue.code]}
    />
  );
}

/** A report's rows of one kind — its errors, warnings or removals — under "Title (n)", a table
 *  only when there are any; `hook` is the attribute each row carries and its value. */
export function ReportTable<Row>({
  title,
  heading,
  rows,
  columns,
  hook
}: {
  title: string;
  heading: 2 | 3;
  rows: Row[];
  columns: IDataColumn<Row & { index: number }>[];
  hook: [`data-${string}`, (row: Row) => string];
}) {
  const Heading = heading === 2 ? 'h2' : 'h3';
  return (
    <section aria-label={title} className="grid gap-2">
      <Heading
        className={cn(
          'font-semibold text-[var(--color-royal-blue)]',
          heading === 2 ? 'text-base' : 'text-sm'
        )}
      >
        {title} ({rows.length})
      </Heading>
      {rows.length > 0 && (
        <DataTable
          label={title}
          columns={columns}
          rows={rows.map((row, index) => ({ ...row, index }))}
          rowKey={(row) => String(row.index)}
          rowProps={(row) =>
            ({ [hook[0]]: hook[1](row) }) as React.HTMLAttributes<HTMLTableRowElement>
          }
          maxHeight="20rem"
        />
      )}
    </section>
  );
}
